"use strict";
// An area's site requirements (docs/SCHEMA.md "Before UAT" §1 and §3): the
// access, hazard and condition tags and the preliminaries ticked on site, by
// hig_accesstag key, plus free-text "Other" entries per category. Stored as a
// row per tick (hig_areatag, hig_visitareatag; DD 2026-10-22), read here as
// the document the screens and pricing use. Before an area has rows, the
// JSON v1 column they replace (hig_sitereqs, hig_conditiontags) is read. Pure.
Object.defineProperty(exports, "__esModule", { value: true });
exports.retiredTicks = exports.hireMatches = exports.removeOther = exports.toggleTag = exports.serialiseSiteReqs = exports.PRELIMINARY = void 0;
exports.parseSiteReqs = parseSiteReqs;
exports.addOther = addOther;
exports.tagsForSide = tagsForSide;
exports.prelimLine = prelimLine;
exports.deskRequirements = deskRequirements;
exports.readSiteReqs = readSiteReqs;
exports.tickWrites = tickWrites;
const SITE_REQS_SCHEMA_VERSION = 1;
/** The category whose tags are preliminaries: priced at the desk as Prelims [051P] lines. */
exports.PRELIMINARY = 'Preliminary';
const emptySiteReqs = () => ({ schemaVersion: SITE_REQS_SCHEMA_VERSION, tags: [], other: [] });
/** The stored text as a document. Empty = nothing ticked; another version is refused rather than misread. */
function parseSiteReqs(text) {
    if (!text)
        return emptySiteReqs();
    const parsed = JSON.parse(text);
    if (parsed.schemaVersion !== SITE_REQS_SCHEMA_VERSION) {
        throw new Error(`This area's site requirements are in a format this app version can't read (schemaVersion ${String(parsed.schemaVersion)}).`);
    }
    return { schemaVersion: SITE_REQS_SCHEMA_VERSION, tags: parsed.tags ?? [], other: parsed.other ?? [] };
}
const serialiseSiteReqs = (doc) => JSON.stringify(doc);
exports.serialiseSiteReqs = serialiseSiteReqs;
const toggleTag = (doc, key) => ({
    ...doc,
    tags: doc.tags.includes(key) ? doc.tags.filter((k) => k !== key) : [...doc.tags, key],
});
exports.toggleTag = toggleTag;
/** Add an "Other" entry; blank or already there does nothing. */
function addOther(doc, category, label) {
    const text = label.trim();
    if (!text || doc.other.some((o) => o.category === category && o.label === text))
        return doc;
    return { ...doc, other: [...doc.other, { category, label: text }] };
}
const removeOther = (doc, category, label) => ({
    ...doc,
    other: doc.other.filter((o) => !(o.category === category && o.label === label)),
});
exports.removeOther = removeOther;
/** The tags offered on an area of this side (Internal / External), grouped by category in list order. */
function tagsForSide(tags, side) {
    const offered = tags.filter((t) => !t.side || t.side === 'Both' || !side || t.side === side);
    const groups = new Map();
    for (const tag of [...offered].sort((a, b) => a.sortOrder - b.sortOrder)) {
        groups.set(tag.category, [...(groups.get(tag.category) ?? []), tag]);
    }
    return [...groups].map(([category, list]) => ({ category, tags: list }));
}
/** An "Other" preliminary has no tag, so its key is its text, which the measurer can't change once added (only remove). */
const otherPrelimKey = (label) => `other:${label}`;
/** A typed entry's name for its row: a preliminary's is the key its line keeps (#418); another category's says which. */
const otherName = (o) => (o.category === exports.PRELIMINARY ? otherPrelimKey(o.label) : `other:${o.category}:${o.label}`);
/**
 * The area's line pricing a preliminary: the one added for it, whatever it is called now (#418);
 * else, for lines added before lines kept that link, an unlinked one with the preliminary's name.
 */
function prelimLine(prelim, lines) {
    const named = prelim.label.trim().toLowerCase();
    return lines.find((l) => l.siteItem === prelim.key) ?? lines.find((l) => !l.siteItem && l.description.trim().toLowerCase() === named);
}
/** What an area's requirements mean at the desk: preliminaries to price, and hire to suggest. */
function deskRequirements(doc, tags) {
    const byKey = new Map(tags.map((t) => [t.key, t]));
    const ticked = doc.tags.map((k) => byKey.get(k)).filter((t) => t !== undefined);
    return {
        prelims: [
            ...ticked.filter((t) => t.category === exports.PRELIMINARY).map((t) => ({ key: t.key, label: t.label })),
            ...doc.other.filter((o) => o.category === exports.PRELIMINARY).map((o) => ({ key: otherPrelimKey(o.label), label: o.label })),
        ],
        hire: ticked.filter((t) => t.hireSearch).map((t) => ({ tag: t.label, search: t.hireSearch })),
        access: [
            ...ticked.filter((t) => t.category !== exports.PRELIMINARY).map((t) => t.label),
            ...doc.other.filter((o) => o.category !== exports.PRELIMINARY).map((o) => o.label),
        ],
    };
}
/** Hire items whose name contains the search, ignoring case — what a tag suggests. */
const hireMatches = (items, search) => items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
exports.hireMatches = hireMatches;
const sameId = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
/**
 * The ticks as a document: the active rows when the area has any (a tag's in
 * list order, typed ones by category and text), else the JSON. A row whose tag
 * is no longer in the list is left out here — retiredTicks shows it.
 */
function readSiteReqs(source, tags) {
    if (!source.rows.length)
        return parseSiteReqs(source.json);
    const active = source.rows.filter((r) => r.active);
    const ticked = new Set(tags.filter((t) => active.some((r) => sameId(r.tagId, t.id))).map((t) => t.key));
    const other = active
        .filter((r) => !r.tagId && r.category)
        .map((r) => ({ category: r.category, label: r.label }))
        .sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
    const inOrder = [...tags].sort((a, b) => a.sortOrder - b.sortOrder).map((t) => t.key);
    return { schemaVersion: SITE_REQS_SCHEMA_VERSION, tags: inOrder.filter((k) => ticked.has(k)), other };
}
/** Active rows of tags no longer in CRM's list: kept on the area, shown so they can be removed. */
const retiredTicks = (rows, tags) => rows.filter((r) => r.active && r.tagId && !tags.some((t) => sameId(t.id, r.tagId)));
exports.retiredTicks = retiredTicks;
/** Every tick in a document as the row it is. A key not in the list (retired) has no tag id to point at, so has no row. */
function tickRows(doc, tags) {
    const rows = new Map();
    for (const key of doc.tags) {
        const tag = tags.find((t) => t.key === key);
        if (tag?.id)
            rows.set(key, { name: key, tagId: tag.id, label: tag.label, category: null, active: true });
    }
    for (const o of doc.other)
        rows.set(otherName(o), { name: otherName(o), tagId: null, label: o.label, category: o.category, active: true });
    return rows;
}
/**
 * The rows a change from `before` to `after` writes: each new tick active,
 * each one taken off inactive. An area with no rows yet (its ticks still in
 * the JSON) gets a row for every tick, so nothing is lost moving to rows.
 */
function tickWrites(before, after, tags, hasRows) {
    const was = tickRows(before, tags);
    const now = tickRows(after, tags);
    const added = [...now.values()].filter((r) => !hasRows || !was.has(r.name));
    const removed = [...was.values()].filter((r) => !now.has(r.name)).map((r) => ({ ...r, active: false }));
    return [...added, ...removed];
}
